import { isEmpty } from '@npi/buddy-utils';

import { showErrorToast } from 'src/components/Toasts/toast';
import { formatToInternational } from 'src/lib/formatters';
import extractCallingCodeAndNumber from 'src/utils/extractCallingCode';

import {
    HandleFormatNumberCountryChangedParams,
    HandleFormatNumberInputChangedParams,
    HandleFormatNumberWithoutCallingCodeParams,
    HandleInitialCountrySetupParams,
    HandleNumberCallingCodeSameMultipleCountriesParams,
    HandleUpdateSelectedCountryFromNumberParams,
} from '../types';

/*
 * Default country for the phone number field.
 * This is used when no initial value is provided or when the phone number is invalid.
 */
const DEFAULT_COUNTRY = {
    name: 'Ireland',
    country: 'IE',
};

/**
 * Formats a given phone number to international format based on the specified country
 * and updates the provided value setter with the formatted number. If formatting fails,
 * an error toast is displayed to the user.
 *
 * @param number - The phone number to be formatted.
 * @param country - The country code used for formatting the phone number.
 * @param setValue - A function to set the formatted phone number.
 * @returns void
 * @throws Error if the phone number is invalid.
 */
export const formatAndSetValue = (
    number: string,
    country: string,
    setValue: (value: string) => void
) => {
    try {
        const formattedNumber = formatToInternational(number, country);
        setValue(formattedNumber);
    } catch {
        showErrorToast("The user's phone number is invalid. Please update.");
    }
};

/**
 * Formats the phone number based on the selected country's calling code.
 * Updates the value if the calling code does not match the selected country.
 *
 * @param {HandleFormatNumberCountryChangedParams} params - The parameters containing value, country data, selected country name, and setValue function.
 * @returns {void} - No return value.
 * @throws {Error} - Throws an error if the input value or selected country name is invalid.
 */
export const handleFormatNumberCountryChanged = ({
    value,
    countryData,
    selectedCountryName,
    setValue,
}: HandleFormatNumberCountryChangedParams) => {
    if (isEmpty(value) || isEmpty(selectedCountryName)) return;

    const { callingCode, usersNumber } = extractCallingCodeAndNumber(value);
    const countryByName = new Map(
        countryData.map((country) => {
            return [country.name, country];
        })
    );

    const selectedCountry = countryByName.get(selectedCountryName);
    if (!selectedCountry) return;

    if (callingCode !== selectedCountry.callingCode) {
        const formattedNumber = `+${selectedCountry.callingCode}${
            usersNumber ? ` ${usersNumber}` : ''
        }`;
        if (formattedNumber !== value) {
            setValue(formattedNumber);
        }
    }
};

/**
 * Handles the update of the selected country based on the phone number input.
 * If the phone number is empty, no action is taken.
 *
 * @param value - The phone number input value.
 * @param countryData - Array of country data containing calling codes and country names.
 * @param selectedCountryName - The name of the currently selected country.
 * @param areaCode - The area code for the phone number.
 * @param setValue - A function to set the formatted phone number.
 * @param setAreaCode - A function to set the area code.
 * @param setSelectedCountryName - A function to set the selected country name.
 * @returns void
 */
export const handleUpdateSelectedCountryFromNumber = ({
    value,
    countryData,
    selectedCountryName,
    areaCode,
    setValue,
    setAreaCode,
    setSelectedCountryName,
}: HandleUpdateSelectedCountryFromNumberParams) => {
    try {
        const { callingCode, usersNumber } = extractCallingCodeAndNumber(value);
        const inputNumberCountry = countryData.find((country) => {
            return country.callingCode === callingCode;
        });

        let setForCountryName = selectedCountryName || 'Ireland';
        let setForCountryCode = areaCode || 'IE';

        if (
            inputNumberCountry &&
            selectedCountryName !== inputNumberCountry.name &&
            callingCode !== '44'
        ) {
            const number = formatToInternational(usersNumber, inputNumberCountry.country);
            setForCountryName = inputNumberCountry.name;
            setForCountryCode = inputNumberCountry.country;
            setValue(number !== value ? number : value);
        } else if (callingCode === '44' && selectedCountryName !== 'United Kingdom') {
            setForCountryName = 'United Kingdom';
            setForCountryCode = 'GB';
        }

        setAreaCode(setForCountryCode);
        setSelectedCountryName(setForCountryName);
    } catch {
        setSelectedCountryName(selectedCountryName || 'Ireland');
        setAreaCode(areaCode || 'IE');
    }
};

/**
 * Handles the case where multiple countries share the same calling code.
 * If the phone number is invalid, an error toast is displayed to the user.
 *
 * @param value - The phone number input value.
 * @param countryData - Array of country data containing calling codes and country names.
 * @param setValue - A function to set the formatted phone number.
 * @returns void
 */
export const handleNumberCallingCodeSameMultipleCountries = ({
    value,
    countryData,
    setValue,
}: HandleNumberCallingCodeSameMultipleCountriesParams) => {
    const defaultCountry = countryData.find((country) => {
        return country.name === 'United Kingdom';
    });
    try {
        const number = formatToInternational(value, defaultCountry?.country || '');
        setValue(number);
    } catch {
        showErrorToast("The user's phone number is invalid. Please update.");
    }
};

/**
 * Formats the phone number without a calling code and updates the provided value setter.
 * If formatting fails, an error toast is displayed to the user.
 *
 * @param value - The phone number input value.
 * @param countryData - Array of country data containing calling codes and country names.
 * @param selectedCountryName - The name of the currently selected country.
 * @param setValue - A function to set the formatted phone number.
 * @returns void
 */
export const handleFormatNumberWithoutCallingCode = ({
    value,
    countryData,
    selectedCountryName,
    setValue,
}: HandleFormatNumberWithoutCallingCodeParams) => {
    try {
        const inputNumberCountry = countryData.find((country) => {
            return country.name === selectedCountryName;
        });
        const formattedNumber = formatToInternational(value, inputNumberCountry?.country || '');
        setValue(formattedNumber);
    } catch {
        showErrorToast("The user's phone number is invalid. Please update.");
    }
};

/**
 * Handles the change of phone number input and formats it accordingly.
 * If the phone number is empty, no action is taken.
 *
 * @param value - The phone number input value.
 * @param countryData - Array of country data containing calling codes and country names.
 * @param selectedCountryName - The name of the currently selected country.
 * @param setValue - A function to set the formatted phone number.
 * @param setAreaCode - A function to set the area code.
 * @param areaCode - The area code for the phone number.
 * @param setSelectedCountryName - A function to set the selected country name.
 * @returns void
 */
export const handleFormatNumberInputChanged = ({
    value,
    initialValue,
    countryData,
    selectedCountryName,
    setValue,
    setAreaCode,
    areaCode,
    setSelectedCountryName,
}: HandleFormatNumberInputChangedParams) => {
    if (isEmpty(value)) return;
    if (value === initialValue) return;

    if (value.trim().includes('+')) {
        handleUpdateSelectedCountryFromNumber({
            value,
            countryData,
            selectedCountryName,
            areaCode,
            setValue,
            setAreaCode,
            setSelectedCountryName,
        });
    }

    if (value[0] === '0') {
        handleFormatNumberWithoutCallingCode({
            value,
            countryData,
            selectedCountryName,
            setValue,
        });
    } else if (value.trim().includes('+')) {
        try {
            const { callingCode } = extractCallingCodeAndNumber(value);
            const selectedCountry = countryData.find((country) => {
                return country.name === selectedCountryName;
            });
            if (selectedCountry?.callingCode === '44' && callingCode !== '44') {
                handleNumberCallingCodeSameMultipleCountries({
                    value,
                    countryData,
                    setValue,
                });
            }
        } catch {
            showErrorToast("The user's phone number is invalid. Please update.");
        }
    }
};

/**
 * Validates the phone number input and sets the error state if invalid.
 * If the phone number is valid, it updates the value state.
 *
 * @param value - The phone number input value.
 * @param onValidatePhoneNumber - A function to validate the phone number.
 * @param setError - A function to set the error state.
 * @param setValue - A function to set the formatted phone number.
 * @returns void
 */
export const validatePhoneNumber = async ({
    value,
    onValidatePhoneNumber,
    setError,
    setValue,
}: {
    value: string;
    onValidatePhoneNumber: (
        phoneNumber: string,
        onError: () => void,
        onSuccess: (number: string) => void
    ) => Promise<void>;
    setError: (error: string | null) => void;
    setValue: (value: string) => void;
}) => {
    if (value?.length > 0) {
        const trimmedPhoneNumber = value.trim();

        await onValidatePhoneNumber(
            trimmedPhoneNumber,
            () => {
                return setError('Mobile number must be valid');
            },
            (number: string) => {
                setValue(number);
                setError(null);
            }
        );
    }
};

/**
 * Handles the initial setup of the country based on the provided phone number.
 * If the phone number is invalid, the default country is set.
 *
 * @param initialValue - The initial phone number input value.
 * @param countryData - Array of country data containing calling codes and country names.
 * @param loadingCountries - A boolean indicating if the countries are still loading.
 * @param setCountries - A function to set the country data.
 * @param setSelectedCountryName - A function to set the selected country name.
 * @param setAreaCode - A function to set the area code.
 * @returns void
 */
export const handleInitialCountrySetup = ({
    initialValue,
    countryData,
    loadingCountries,
    setCountries,
    setSelectedCountryName,
    setAreaCode,
}: HandleInitialCountrySetupParams) => {
    if (!countryData && loadingCountries) return;

    setCountries(countryData);

    const countryByCallingCode = new Map(
        countryData.map((country) => {
            return [country.callingCode, country];
        })
    );

    try {
        if (!initialValue) {
            setSelectedCountryName(DEFAULT_COUNTRY.name);
            setAreaCode(DEFAULT_COUNTRY.country);
            return;
        }

        const { callingCode } = extractCallingCodeAndNumber(initialValue);
        if (!callingCode) throw new Error('Invalid format');

        const matchedCountry = countryByCallingCode.get(callingCode);
        if (!matchedCountry) throw new Error('Country not found');

        setSelectedCountryName(matchedCountry.name);
        setAreaCode(matchedCountry.country);
    } catch {
        setSelectedCountryName(DEFAULT_COUNTRY.name);
        setAreaCode(DEFAULT_COUNTRY.country);
    }
};
